import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { useToast } from '@/components/ui/use-toast';
import { supabase } from '@/lib/supabase';
import { Loader2, Search, AlertCircle } from 'lucide-react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from "@/components/ui/pagination";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";

interface HubspotContact {
  id: string;
  associatedcompanyid: string | null;
  email: string;
  firstname: string | null;
  lastname: string | null;
  phone: string | null;
  mobilephone: string | null;
  jobtitle: string | null;
  company_id: string | null;
  industry: string | null;
}

interface ImportHubspotContactDialogProps {
  customerId: string;
  isOpen: boolean;
  onClose: () => void;
  onContactImported: () => void;
}

export default function ImportHubspotContactDialog({
  customerId,
  isOpen,
  onClose,
  onContactImported
}: ImportHubspotContactDialogProps) {
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(false);
  const [hubspotContacts, setHubspotContacts] = useState<HubspotContact[]>([]);
  const [filteredContacts, setFilteredContacts] = useState<HubspotContact[]>([]);
  const [selectedContact, setSelectedContact] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [noExternalIdsFound, setNoExternalIdsFound] = useState(false);
  const itemsPerPage = 5;
  const { toast } = useToast();

  useEffect(() => {
    if (isOpen) {
      fetchHubspotContacts();
    } else {
      // Reset state when dialog closes
      setSelectedContact(null);
      setSearchTerm("");
      setCurrentPage(1);
      setNoExternalIdsFound(false);
    }
  }, [isOpen]);

  useEffect(() => {
    if (hubspotContacts.length > 0) {
      filterContacts();
    }
  }, [searchTerm, currentPage, hubspotContacts]);

  const fetchHubspotContacts = async () => {
    try {
      setLoading(true);
      setNoExternalIdsFound(false);
      
      // Abordagem direta: buscar os external_ids primeiro
      const { data: externalIdData, error: externalIdError } = await supabase
        .from('external_ids')
        .select('external_id')
        .eq('entity_id', customerId)
        .eq('system_name', 'hubspot');
      
      if (externalIdError) {
        console.error('Erro ao buscar IDs externos:', externalIdError);
        // Fallback: buscar todos os contatos do Hubspot
        await fetchAllHubspotContacts();
        return;
      }
      
      if (!externalIdData || externalIdData.length === 0) {
        console.log('Nenhum ID externo encontrado para o cliente:', customerId);
        // Fallback: buscar todos os contatos do Hubspot
        await fetchAllHubspotContacts();
        return;
      }
      
      // Extrair os IDs externos
      const externalIds = externalIdData.map(item => item.external_id);
      console.log('IDs externos encontrados:', externalIds);
      
      // Buscar contatos que tenham associatedcompanyid igual a algum dos externalIds
      const { data: contacts, error: contactsError } = await supabase
        .from('hubspotcontacts')
        .select('*')
        .in('associatedcompanyid', externalIds);
      
      if (contactsError) {
        console.error('Erro ao buscar contatos do Hubspot:', contactsError);
        // Fallback: buscar todos os contatos do Hubspot
        await fetchAllHubspotContacts();
        return;
      }
      
      if (!contacts || contacts.length === 0) {
        console.log('Nenhum contato do Hubspot encontrado para este cliente');
        // Fallback: buscar todos os contatos do Hubspot
        await fetchAllHubspotContacts();
        return;
      }
      
      console.log('Contatos encontrados:', contacts);
      setHubspotContacts(contacts);
      filterContacts(contacts);
    } catch (error) {
      console.error('Erro ao buscar contatos do HubSpot:', error);
      toast({
        variant: 'destructive',
        title: 'Erro ao carregar contatos',
        description: 'Não foi possível carregar os contatos relacionados ao cliente no HubSpot.',
      });
      setHubspotContacts([]);
      setFilteredContacts([]);
    } finally {
      setLoading(false);
    }
  };

  // Nova função para buscar todos os contatos do Hubspot quando não há IDs externos
  const fetchAllHubspotContacts = async () => {
    try {
      console.log('Buscando todos os contatos do Hubspot');
      
      // Buscar até 100 contatos do Hubspot (limite para evitar sobrecarga)
      const { data: contacts, error: contactsError } = await supabase
        .from('hubspotcontacts')
        .select('*')
        .limit(100);
      
      if (contactsError) {
        console.error('Erro ao buscar todos os contatos do Hubspot:', contactsError);
        setHubspotContacts([]);
        setFilteredContacts([]);
        setNoExternalIdsFound(true);
        return;
      }
      
      if (!contacts || contacts.length === 0) {
        console.log('Nenhum contato encontrado no Hubspot');
        setHubspotContacts([]);
        setFilteredContacts([]);
        setNoExternalIdsFound(true);
        return;
      }
      
      console.log(`Encontrados ${contacts.length} contatos do Hubspot`);
      setHubspotContacts(contacts);
      filterContacts(contacts);
      // Set this to true when we find contacts, but they weren't associated with the customer
      setNoExternalIdsFound(true);
    } catch (error) {
      console.error('Erro ao buscar todos os contatos do Hubspot:', error);
      setHubspotContacts([]);
      setFilteredContacts([]);
      setNoExternalIdsFound(true);
    }
  };

  const filterContacts = (contacts = hubspotContacts) => {
    let filtered = [...contacts];
    
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      filtered = filtered.filter(contact => 
        (contact.email && contact.email.toLowerCase().includes(searchLower)) ||
        (contact.firstname && contact.firstname.toLowerCase().includes(searchLower)) ||
        (contact.lastname && contact.lastname.toLowerCase().includes(searchLower)) ||
        (contact.jobtitle && contact.jobtitle.toLowerCase().includes(searchLower))
      );
    }
    
    setTotalPages(Math.max(1, Math.ceil(filtered.length / itemsPerPage)));
    
    // Paginate the results
    const start = (currentPage - 1) * itemsPerPage;
    const paginatedResults = filtered.slice(start, start + itemsPerPage);
    
    setFilteredContacts(paginatedResults);
  };

  const handleImport = async () => {
    if (!selectedContact) {
      toast({
        variant: 'destructive',
        title: 'Nenhum contato selecionado',
        description: 'Selecione um contato para importar.',
      });
      return;
    }
    
    try {
      setLoading(true);
      
      // Find the selected contact
      const contact = hubspotContacts.find(c => c.id === selectedContact);
      
      if (!contact) {
        throw new Error('Contato não encontrado');
      }
      
      // Check if contact already exists
      const { data: existingContact, error: checkError } = await supabase
        .from('customer_contacts')
        .select('id')
        .eq('customer_id', customerId)
        .eq('email', contact.email)
        .eq('is_active', true)
        .maybeSingle();
        
      if (checkError) throw checkError;
      
      if (existingContact) {
        toast({
          variant: 'destructive',
          title: 'Contato já existe',
          description: 'Um contato com este e-mail já existe para este cliente.',
        });
        return;
      }
      
      // Create new contact from HubSpot data
      const { data: newContact, error: createError } = await supabase
        .from('customer_contacts')
        .insert({
          customer_id: customerId,
          first_name: contact.firstname || '',
          last_name: contact.lastname || '',
          email: contact.email || '',
          phone: contact.mobilephone || contact.phone || '', // Prioritize mobilephone
          job_title: contact.jobtitle || null,
          is_active: true, // Assume active on import
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        })
        .select()
        .single();

      if (createError) throw createError;
      
      // Após criar o contato, cria um registro na tabela external_ids
      if (newContact) {
        const { error: externalIdError } = await supabase
          .from('external_ids')
          .insert({
            entity_name: 'customer_contacts',
            entity_id: newContact.id,
            system_name: 'hubspot',
            external_id: contact.id
          });
          
        if (externalIdError) {
          console.error('Erro ao criar registro external_id para o contato:', externalIdError);
          // Não impede o fluxo principal se houver erro aqui
        }
        
        // Verificar se já existe um vínculo para a empresa do Hubspot
        if (contact.associatedcompanyid) {
          // Verificar se esse vínculo já existe
          const { data: existingCompanyLink, error: checkCompanyError } = await supabase
            .from('external_ids')
            .select('id')
            .eq('entity_id', customerId)
            .eq('system_name', 'hubspot')
            .eq('external_id', contact.associatedcompanyid)
            .maybeSingle();
            
          if (!checkCompanyError && !existingCompanyLink) {
            // Criar vínculo para a empresa do Hubspot
            const { error: companyLinkError } = await supabase
              .from('external_ids')
              .insert({
                entity_name: 'customers',
                entity_id: customerId,
                system_name: 'hubspot',
                external_id: contact.associatedcompanyid
              });
              
            if (companyLinkError) {
              console.error('Erro ao criar vínculo para empresa do Hubspot:', companyLinkError);
              // Não impede o fluxo principal
            } else {
              console.log('Vínculo criado para empresa do Hubspot:', contact.associatedcompanyid);
            }
          }
        }
      }
      
      toast({
        title: 'Contato importado',
        description: 'O contato foi importado com sucesso do HubSpot.',
      });
      
      onContactImported();
      onClose();
    } catch (error) {
      console.error('Erro ao importar contato:', error);
      toast({
        variant: 'destructive',
        title: 'Erro ao importar contato',
        description: 'Não foi possível importar o contato. Por favor, tente novamente.',
      });
    } finally {
      setLoading(false);
    }
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[800px] w-[90vw]">
        <DialogHeader>
          <DialogTitle>Importar Contato do HubSpot</DialogTitle>
        </DialogHeader>
        
        <div className="space-y-4 py-2">
          {noExternalIdsFound ? (
            <Alert>
              <AlertCircle className="h-4 w-4" />
              <AlertTitle>Sem conexão específica com o HubSpot</AlertTitle>
              <AlertDescription>
                Este cliente não está diretamente vinculado a uma empresa no HubSpot. 
                Estamos mostrando todos os contatos disponíveis no HubSpot. Se encontrar o contato desejado, 
                você pode importá-lo e a relação será estabelecida.
              </AlertDescription>
            </Alert>
          ) : (
            <>
              <div className="flex items-center space-x-2">
                <div className="relative flex-1">
                  <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
                  <Input
                    type="search"
                    placeholder="Buscar por nome, email ou cargo..."
                    className="pl-8"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                </div>
              </div>
              
              {loading ? (
                <div className="flex justify-center p-4">
                  <Loader2 className="h-6 w-6 animate-spin text-primary" />
                </div>
              ) : filteredContacts.length === 0 ? (
                <div className="text-center py-4 text-muted-foreground">
                  Nenhum contato relacionado a este cliente encontrado no HubSpot.
                </div>
              ) : (
                <>
                  <div className="border rounded-md">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead className="w-[50px]">Selecionar</TableHead>
                          <TableHead>Nome</TableHead>
                          <TableHead>Email</TableHead>
                          <TableHead>Cargo</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {filteredContacts.map((contact) => (
                          <TableRow key={contact.id}>
                            <TableCell>
                              <input
                                type="radio"
                                name="selectedContact"
                                value={contact.id}
                                checked={selectedContact === contact.id}
                                onChange={() => setSelectedContact(contact.id)}
                                className="h-4 w-4 rounded border-gray-300"
                              />
                            </TableCell>
                            <TableCell>
                              {contact.firstname || ''} {contact.lastname || ''}
                            </TableCell>
                            <TableCell>{contact.email}</TableCell>
                            <TableCell>{contact.jobtitle || '-'}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                  
                  {totalPages > 1 && (
                    <Pagination>
                      <PaginationContent>
                        <PaginationItem>
                          <PaginationPrevious 
                            onClick={() => handlePageChange(Math.max(1, currentPage - 1))}
                            className={currentPage === 1 ? "pointer-events-none opacity-50" : "cursor-pointer"}
                          />
                        </PaginationItem>
                        
                        {Array.from({ length: totalPages }).map((_, index) => (
                          <PaginationItem key={index}>
                            <PaginationLink
                              onClick={() => handlePageChange(index + 1)}
                              isActive={currentPage === index + 1}
                            >
                              {index + 1}
                            </PaginationLink>
                          </PaginationItem>
                        ))}
                        
                        <PaginationItem>
                          <PaginationNext 
                            onClick={() => handlePageChange(Math.min(totalPages, currentPage + 1))}
                            className={currentPage === totalPages ? "pointer-events-none opacity-50" : "cursor-pointer"}
                          />
                        </PaginationItem>
                      </PaginationContent>
                    </Pagination>
                  )}
                </>
              )}
            </>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancelar
          </Button>
          <Button 
            onClick={handleImport} 
            disabled={!selectedContact || loading}
          >
            {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Importar Contato
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
} 